import React, { FunctionComponent } from "react";
import { DynamicComponentTypes, DynamicLPModule } from "@/types/dto/landing-page/landing-page";
import PageHero from "@/components/common/page-hero/page-hero";
import FeefoSummarySection from "@/components/landing-page/feefo-summary-section";
import PeaceOfMind from "@/components/landing-page/peace-of-mind/peace-of-mind";
import { UnforgettableMomentsComponent } from "@/components/landing-page/unforgettable-moments/unforgettable-moments";
import InterlinkingMenu from "@/components/landing-page/interlinking-menu/interlinking-menu";
import BrochureSection from "@/components/landing-page/brochure-section/brochure-section";
import FaqSectionComponent from "@/components/common/faq/faq";
import crowdRiffGallerySection from "@/components/common/crowdriff-gallery/crowd-riff-gallery-section";
import logoGrid from "@/components/common/logo-grid/logo-grid";
import ReviewSection from "@/components/landing-page/review-section";
import BottomCtaSection from "@/components/landing-page/bottom-cta-section/bottom-cta-section";
import CampaignCardCarousel from "@/components/homepage/campaign-cards/campaign-card-carousel";
import { TravelStyleSection } from "@/components/deals-page/travel-style-section";
import MediaTextCard from "@/components/common/media-text-card/media-text-card";
import FlexibleCardLayout from "@/components/common/flexible-card-layout/flexible-card-layout";
import WistiaVideoCarouselSection from "@/components/common/wistia-video-carousel-section/wistia-video-carousel-section";
import TourHighlightSection from "@/components/destinations-page/tour-highlights-section/tour-highlights-section";
import WistiaCenteredSection from "@/components/common/wistia-centered-section/wistia-centered-section";
import BasePageHero from "@/components/common/page-hero/base-page-hero/base-page-hero";
import HubspotFormShellModule from "@/components/forms/hubspot-form-shell-module/hubspot-form-shell-module";
import TextModule from "@/components/common/text-module/text-module";
import RelatedBlogsPdp from "@/components/product-detail-page/related-blogs/related-blogs";
import ContactFormSectionComponent from "@/components/forms/contact-form-section/contact-form-section";
import FeaturedTours from "@/components/common/featured-tours";
import DynamicModuleContext from "@/components/landing-page/dynamic-module-context";
import TermsAndConditions from "@/components/common/terms-and-conditions";
import OfferSavingsTable from "@/components/landing-page/offer-savings-table/offer-savings-table";
import CountdownTimer from "../countdown-timer/countdown-timer";

interface DynamicComponentProps {
  type: DynamicComponentTypes;
  props: DynamicLPModule;
  moduleRankingPosition: string;
  moduleId?: string;
}

const componentMap: Record<DynamicComponentTypes, FunctionComponent<any>> = {
  heroSplitLayoutTitleSubtitleImageCtAs: PageHero,
  heroSplitLayoutTitleSubtitleFeefoRating: PageHero,
  heroBackgroundImageTitleSubtitleCtAs: BasePageHero,
  heroBackgroundImageLockUp: BasePageHero,
  navigationMenuImageCardGrid: InterlinkingMenu,
  feefoBar: FeefoSummarySection,
  carouselToursManuallySelected: FeaturedTours,
  valuePropsTitleSubtitleIconCards: PeaceOfMind,
  itineraryCardGridImageTitleTourCtAs: UnforgettableMomentsComponent,
  brochureFeatureCtAsImage: BrochureSection,
  brochureFeatureInlineViewOnlineFormImage: BrochureSection,
  faqAccordion: FaqSectionComponent,
  galleryCrowdRiffFeed: crowdRiffGallerySection,
  imageGridMultiColumn: logoGrid,
  reviewsManualEntry3Column: ReviewSection,
  ctaFullWidthCardTitleSubtitleCta: BottomCtaSection,
  campaignCarouselMarketingCardsImageTitleCta: CampaignCardCarousel,
  featuredCategoriesCardGridImageDescriptionCta: TravelStyleSection,
  contentPanelImageText: MediaTextCard,
  carouselOrZigZagImageCta: FlexibleCardLayout,
  tourCarouselFeaturedToursNoPricingImageCTAs: TourHighlightSection,
  videoCarouselWistiaVideos: WistiaVideoCarouselSection,
  textModuleHeaderCopyCtAs: TextModule,
  textModuleTermsConditions: TermsAndConditions,
  videoEmbedWistiaCenteredSection: WistiaCenteredSection,
  leadCaptureFormCenteredPanelHeaderForm: HubspotFormShellModule,
  relatedBlogsSection: RelatedBlogsPdp,
  contactFormTwoColumnSupportingContent: ContactFormSectionComponent,
  offerSavingsTable: OfferSavingsTable,
  countdownTimer: CountdownTimer
};

function DynamicLandingPageComponent({
  type,
  props,
  moduleRankingPosition,
  moduleId
}: DynamicComponentProps) {
  const ComponentToRender = componentMap[type];
  const componentProps = {
    ...props,
    ...(moduleId ? { moduleId } : {}),
    moduleRankingPosition
  };

  return ComponentToRender ? (
    <DynamicModuleContext.Provider value={moduleId}>
      <ComponentToRender {...componentProps} />
    </DynamicModuleContext.Provider>
  ) : null;
}

export default DynamicLandingPageComponent;
